import React, { useEffect, useMemo, useState } from "react";
import { ContentContainer } from "../../../components/layout";
import {
  Button,
  Modal,
  MultiSelect,
  Select,
  Textarea,
  Textfield,
} from "../../../components/core";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  AGENTS_WITH_FILTER,
  BRANCHES,
  PARTNERS,
} from "../../../constants/graphql/partner-agent-queries";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import {
  triggerTypeValues,
  walletTransactionTypeValues,
} from "./constants/dropdownValues";
import { PARTNER_WALLET_HISTORY } from "../../../constants/graphql/partner-wallet-history";
import { Strings } from "../../../constants/strings";
import { formatDateYYYYMMDD } from "../../../algorithms/date";
import PartnerWalletSummaryCards from "./components/summary-cards";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import PartnerWalletTable from "./components/wallet-table";
import { getAccountIdByFilter } from "./reusable/helpers";

export const maxDate = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d;
})();

const WalletHistoryReport = () => {
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const operatorId = user?.operator_id || "";
  const operatorName = user?.company;

  const [filterVariables, setFilterVariables] = useState({
    transactionNumber: "",
    transactionPurposeId: -1,
    transactionTypeId: -1,
    walletFilter: 1,
    startDate: maxDate,
    endDate: new Date(),
    accountId: null,
    selectedPartnerId: -1,
    selectedPartnerBranch: [],
    selectedPartnerAgent: -1,
    selectedWallet: null,
  });
  const [allBranchesSelected, setAllBranchesSelected] = useState(false);

  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  const [transactions, setTransactions] = useState([]);
  const [isClicked, setIsClicked] = useState(false);

  //   gql
  const [findBranchesByPartnerId] = useLazyQuery(BRANCHES);
  const { data: allPartners } = useQuery(PARTNERS, {
    variables: {
      operatorId: operatorId,
    },
  });
  const [fetchAgents, { data: filteredAgents, loading: agentsLoading }] =
    useLazyQuery(AGENTS_WITH_FILTER);

  const [
    fetchAllPartnerWalletReports,
    { data: allWalletTransactions, loading: loadingWalletReport },
  ] = useLazyQuery(PARTNER_WALLET_HISTORY);

  const walletFilterOptions = [
    { name: "Operator Level", value: 1 },
    { name: "Partner Level", value: 2 },
    { name: "Branch Level", value: 3 },
    { name: "Agent Level", value: 4 },
  ];

  const transactionPurposes = Object.entries(walletTransactionTypeValues).map(
    ([key, label]) => ({
      value: parseInt(key),
      name: label,
    }),
  );

  const transactionTypes = Object.entries(triggerTypeValues).map(
    ([key, label]) => ({
      value: parseInt(key),
      name: label,
    }),
  );

  const validateFilterVariables = (variables) => {
    if (
      filterVariables.walletFilter == 2 &&
      variables.selectedPartnerId == -1
    ) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_error_title_search_criteria,
        message: "Please select a partner for Partner Level wallet filter.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
            />
          </div>
        ),
      });
      return false;
    }

    if (
      filterVariables.walletFilter == 3 &&
      variables.selectedPartnerBranch?.length === 0
    ) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_error_title_search_criteria,
        message: "Please select a branch for Branch Level wallet filter.",
        open: true,
        actions: (
          <div className="w-full">
            <Button
              onClick={() => setValidation({ open: false })}
              value="OK"
              className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
            />
          </div>
        ),
      });
      return false;
    }

    if (filterVariables.walletFilter == 4) {
      if (!variables.selectedPartnerId || variables.selectedPartnerId == -1) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: Strings.popup_error_title_search_criteria,
          message: "Please select a partner for Agent Level wallet filter.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
              />
            </div>
          ),
        });
        return false;
      }
      if (variables.selectedPartnerBranch?.length === 0) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: Strings.popup_error_title_search_criteria,
          message: "Please select a branch for Agent Level wallet filter.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
              />
            </div>
          ),
        });
        return false;
      }

      if (!variables.selectedPartnerAgent) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: Strings.popup_error_title_search_criteria,
          message: "Please select an agent for Agent Level wallet filter.",
          open: true,
          actions: (
            <div className="w-full">
              <Button
                onClick={() => setValidation({ open: false })}
                value="OK"
                className="p-2 w-full bg-primary-primaryYellow border-0 text-secondary-purpleGray"
              />
            </div>
          ),
        });
        return false;
      }
    }
    return true;
  };

  const buildFilterVariables = () => {
    const updatedVars = {
      operatorId: operatorId,
      walletFilter: filterVariables.walletFilter,
    };

    if (filterVariables.walletFilter == 1) {
      updatedVars.walletId = operatorId;
    } else if (filterVariables.walletFilter == 2) {
      if (filterVariables.selectedPartnerId) {
        updatedVars.walletId = filterVariables.selectedPartnerId;
      }
    } else if (filterVariables.walletFilter == 3) {
      if (filterVariables.selectedPartnerBranch.length > 0) {
        const branches = filterVariables.selectedPartnerBranch.map(
          (b) => b.value,
        );
        updatedVars.walletIds = branches;
      }
    } else if (filterVariables.walletFilter == 4) {
      if (
        filterVariables.selectedPartnerAgent !== null ||
        filterVariables.selectedPartnerAgent != -1
      ) {
        updatedVars.walletIds = filterVariables.selectedPartnerAgent;
      }
    }

    if (filterVariables.startDate) {
      updatedVars.startDate = formatDateYYYYMMDD(filterVariables.startDate);
    }

    if (filterVariables.endDate) {
      updatedVars.endDate = formatDateYYYYMMDD(filterVariables.endDate);
    }

    // if (filterVariables.walletFilter) {
    //   updatedVars.walletFilter = filterVariables.walletFilter;
    // }

    if (filterVariables.transactionNumber !== "") {
      updatedVars.transactionNumber = filterVariables.transactionNumber;
    }

    if (filterVariables.transactionPurposeId !== -1) {
      updatedVars.triggerTypeNo = filterVariables.transactionPurposeId;
    }

    if (filterVariables.transactionTypeId !== -1) {
      updatedVars.walletTransactionTypeNo = filterVariables.transactionTypeId;
    }

    return updatedVars;
  };

  const handleSearch = () => {
    // Validate first
    const validateFilters = validateFilterVariables(filterVariables);

    if (!validateFilters) {
      setIsClicked(false);
      setTransactions([]);
      return;
    }

    // Clear old data
    setTransactions([]);
    setIsClicked(true);

    // Build variables
    const variables = buildFilterVariables();
    // Execute query - only once
    fetchAllPartnerWalletReports({
      variables: variables,
    });
    // .then((resp) => {
    //   // DEBUG

    //   if (resp?.data?.partnerWalletHistories) {
    //     setTransactions(resp.data.partnerWalletHistories);
    //   } else {
    //     console.warn("No partnerWalletHistories in response");
    //     setTransactions([]);
    //   }
    // })
    // .catch((err) => {
    //   console.error("=== ERROR FETCHING WALLET REPORTS ===", err);
    //   setTransactions([]);
    // });
  };

  useEffect(() => {
    if (allWalletTransactions && allWalletTransactions.partnerWalletHistories) {
      setTransactions(allWalletTransactions.partnerWalletHistories);
    }
  }, [allWalletTransactions]);

  const handleFilterChange = (e, field) => {
    let value = e.target?.value !== undefined ? e.target.value : e.value;
    setIsClicked(false);
    setTransactions([]);
    if (field === "startDate" || field === "endDate") {
      value = value ? new Date(value) : null;
      setFilterVariables((prev) => ({
        ...prev,
        [field]: value,
      }));
      return;
    }

    if (field === "walletFilter") {
      setFilterVariables({
        ...filterVariables,
        [field]: value,
        selectedPartnerId: -1,
        selectedPartnerBranch: [],
        selectedPartnerAgent: null,
      });
      return;
    }

    // KEY FIX: Reset branch and agent when partner changes
    if (field === "selectedPartnerId") {
      value = e.value || e.target.value;
      setFilterVariables((prev) => ({
        ...prev,
        [field]: value,
        selectedPartnerBranch: [],
        selectedPartnerAgent: null,
      }));
      return;
    }

    if (field === "selectedPartnerBranch") {
      // value = e.value;
      setFilterVariables((prev) => ({
        ...prev,
        [field]: value,
        selectedPartnerAgent: null,
      }));
      return;
    }

    setFilterVariables({
      ...filterVariables,
      [field]: value,
    });
  };

  const [branchList, setBranchList] = useState([]);

  useEffect(() => {
    if (
      filterVariables.selectedPartnerId &&
      filterVariables.selectedPartnerId !== -1
    ) {
      findBranchesByPartnerId({
        variables: {
          operatorId: operatorId,
          partnerId: filterVariables.selectedPartnerId,
        },
      }).then((resp) => {
        // setBranchList(
        //   // { value: 999, label: "-- All Branches --" },

        //   resp.data.partnerBranches.map((x) => ({
        //     value: x.partnerBranchId,
        //     label: x.partnerBranch,
        //   })),
        // );
        // setBranchList([
        //   { value: 999, label: "-- All Branches --" },
        //   ...resp.data.partnerBranches.map((x) => ({
        //     value: x.partnerBranchId,
        //     label: x.partnerBranch,
        //   })),
        // ]);

        setBranchList(resp.data.partnerBranches);
      });
    } else {
      setBranchList([]);
    }
  }, [filterVariables.selectedPartnerId]);

  useEffect(() => {
    // fetch agents when branch destination wallet is selected
    if (filterVariables.walletFilter == 4) {
      fetchAgents({
        variables: {
          operatorId: operatorId,
          partnerId: filterVariables.selectedPartnerId,
          partnerBranchId: filterVariables.selectedPartnerBranch,
        },
      });
    }
  }, [
    filterVariables.walletFilter,
    filterVariables.selectedPartnerId,
    filterVariables.selectedPartnerBranch,
  ]);

  const partnerList = useMemo(() => {
    return allPartners?.partners.map((x) => ({
      value: x.partnerId,
      name: x.companyNickname,
    }));
  }, [allPartners]);

  const agentList = useMemo(() => {
    return filteredAgents?.agentsWithFilter.map((x) => ({
      value: x.partnerAgentId,
      name: `${x.agentMobile} (${x.firstName} ${x.lastName})`,
    }));
  }, [filteredAgents]);

  const memoizedBranchList = useMemo(() => {
    if (branchList.length === 0) return [];

    const branches = branchList.map((x) => ({
      value: x.partnerBranchId,
      label: x.partnerBranch,
    }));

    if (!allBranchesSelected) {
      branches.unshift({
        value: 0,
        label: "All Branches",
      });
    }

    return branches;
  }, [branchList, allBranchesSelected]);

  useEffect(() => {
    if (filterVariables.walletFilter == 3) {
      const selected = filterVariables.selectedPartnerBranch;
      const allBranchOptions = memoizedBranchList;
      // const allBranchOptions = branchList || [];

      const hasAllBranchesValue = selected.some((item) => item.value == 0);
      if (hasAllBranchesValue) {
        setAllBranchesSelected(true);
      } else if (selected.length === 0) {
        setAllBranchesSelected(false);
      } else if (selected.length !== allBranchOptions.length) {
        setAllBranchesSelected(false);
      } else {
        setAllBranchesSelected(true);
      }
    }
  }, [
    filterVariables.selectedPartnerBranch,
    filterVariables.walletFilter,
    memoizedBranchList,
  ]);

  // NEW: Auto-select all branches when "All Branches" is selected
  useEffect(() => {
    if (allBranchesSelected && memoizedBranchList) {
      const selectedBranches = memoizedBranchList.filter(
        (branch) => branch.value != 0,
      );

      setFilterVariables((prev) => ({
        ...prev,
        selectedPartnerBranch: selectedBranches,
      }));
    }
  }, [allBranchesSelected, memoizedBranchList]);

  const getSelectedWalletId = (variables) => {
    if (variables.walletFilter == 1) {
      return operatorId;
    } else if (variables.walletFilter == 2) {
      return variables.selectedPartnerId;
    } else if (variables.walletFilter == 3) {
      return variables.selectedPartnerBranch;
    } else if (variables.walletFilter == 4) {
      // const mappedAgents = variables.selectedPartnerAgent.map((a) => a.value);
      return variables.selectedPartnerAgent;
    }
    return null;
  };

  const transformWalletTransactions = (
    transactions,
    filterVariables,
    triggerTypeValues = {},
  ) => {
    if (!transactions || transactions.length === 0) return [];

    const selectedWalletId = getSelectedWalletId(filterVariables);

    return transactions.map((transaction) => {
      const sourceAccount = transaction.debitAccountDetails;
      const destAccount = transaction.creditAccountDetails;

      const isDebit =
        selectedWalletId == transaction.debitAccountDetails?.accountId;
      const isCredit =
        selectedWalletId == transaction.creditAccountDetails?.accountId;
      const isIncoming = isCredit;

      // Get the balance for the searched account
      let selectedAccountBalance = 0;
      if (isDebit) {
        selectedAccountBalance = parseFloat(transaction.debitWalletBalance);
      } else if (isCredit) {
        selectedAccountBalance = parseFloat(transaction.creditWalletBalance);
      }

      const detailsText = `From ${sourceAccount?.accountName || "Unknown"} → To ${
        destAccount?.accountName || "Unknown"
      }`;

      return {
        ...transaction,
        transactionReferenceNo: transaction.appReferenceId,
        transactionType:
          triggerTypeValues[transaction.triggerTypeNo] ||
          `Type ${transaction.triggerTypeNo}`,
        detailsName: detailsText,
        detailsId: sourceAccount?.walletId,
        debit: isDebit ? parseFloat(transaction.transactionAmount) : null,
        credit: isCredit ? parseFloat(transaction.transactionAmount) : null,
        balance: selectedAccountBalance,
        isIncoming,
        sourceAccount,
        destAccount,
        isInvolved: isDebit || isCredit,
      };
    });
  };

  const transformedTransactions = useMemo(() => {
    return transformWalletTransactions(
      transactions,
      filterVariables,
      triggerTypeValues,
    );
  }, [
    transactions,
    filterVariables.walletFilter,
    filterVariables.selectedPartnerId,
    filterVariables.selectedPartnerBranch,
    filterVariables.selectedPartnerAgent,
  ]);
  const handleReset = () => {
    setFilterVariables({
      transactionNumber: "",
      transactionPurposeId: -1,
      transactionTypeId: -1,
      walletFilter: 1,
      startDate: maxDate,
      endDate: new Date(),
      accountId: null,
      selectedPartnerId: -1,
      selectedPartnerBranch: [],
      selectedPartnerAgent: null,
      selectedWallet: null,
    });
    setTransactions([]);
    setIsClicked(false);
    setBranchList([]);
  };

  return (
    <ContentContainer containerTitle="Wallet History Report">
      <div className="grid grid-cols-12 md:grid-cols-6">
        <div className="col-span-12 md:col-span-6 space-y-1">
          <h2 className={"font-bold"}>Wallet Details</h2>

          {/* Wallet Filter */}
          <div className="col-span-1 md:col-span-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <div className="col-span-1 md:col-span-3">
                <p className="text-base">Wallet Level:</p>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Select
                  items={walletFilterOptions}
                  value={filterVariables.walletFilter}
                  onChange={(e) => {
                    handleFilterChange(e, "walletFilter");
                  }}
                  inputClassName="w-full"
                  placeholder="Select wallet level"
                />
              </div>
            </div>
          </div>

          {/* WALLET LEVEL 1 */}
          {filterVariables.walletFilter == 1 && (
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Wallet:</p>
                </div>
                <div className="col-span-12 md:col-span-5 font-semibold">
                  {operatorName}
                </div>
              </div>
            </div>
          )}

          {/* WALLET LEVEL 2 - Partner */}
          {filterVariables.walletFilter == 2 && (
            <div className="col-span-1 md:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <div className="col-span-1 md:col-span-3">
                  <p className="text-base">Partner:</p>
                </div>
                <div className="col-span-12 md:col-span-5">
                  <Select
                    items={[
                      { name: "-- Select partner --", value: -1 },
                      ...(partnerList || []),
                    ]}
                    className={"col-span-1 md:col-span-5"}
                    placeholder="Select partner"
                    value={filterVariables.selectedPartnerId}
                    onChange={(e) => {
                      handleFilterChange(e, "selectedPartnerId");
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* WALLET LEVEL 3 - Branch */}
          {filterVariables.walletFilter == 3 && (
            <>
              <div className="col-span-1 md:col-span-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 md:col-span-3">
                    <p className="text-base">Partner:</p>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <Select
                      items={[
                        { name: "-- Select partner --", value: -1 },
                        ...(partnerList || []),
                      ]}
                      className={"col-span-1 md:col-span-5"}
                      placeholder="Select partner"
                      value={filterVariables.selectedPartnerId}
                      onChange={(e) => {
                        handleFilterChange(e, "selectedPartnerId");
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-1 md:col-span-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 md:col-span-3">
                    <p className="text-base">Branch:</p>
                  </div>
                  <MultiSelect
                    items={memoizedBranchList}
                    disabled={
                      filterVariables.selectedPartnerId &&
                      filterVariables.selectedPartnerId !== -1
                        ? false
                        : true
                    }
                    className={"col-span-1 md:col-span-5"}
                    placeholder="Select partner branch"
                    value={filterVariables.selectedPartnerBranch}
                    onChange={(e) => {
                      handleFilterChange(e, "selectedPartnerBranch");
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {/* WALLET LEVEL 4 - Agent */}
          {filterVariables.walletFilter == 4 && (
            <>
              <div className="col-span-1 md:col-span-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 md:col-span-3">
                    <p className="text-base">Partner:</p>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <Select
                      items={[
                        { name: "-- Select partner --", value: -1 },
                        ...(partnerList || []),
                      ]}
                      className={"col-span-1 md:col-span-5"}
                      placeholder="Select partner"
                      value={filterVariables.selectedPartnerId}
                      onChange={(e) => {
                        handleFilterChange(e, "selectedPartnerId");
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-1 md:col-span-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 md:col-span-3">
                    <p className="text-base">Branch:</p>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <Select
                      defaultOption={{
                        name: "-- Select Partner Branch --",
                        value: -1,
                      }}
                      items={branchList.map((x) => ({
                        value: x.partnerBranchId,
                        name: x.partnerBranch,
                      }))}
                      className={"col-span-1 md:col-span-5"}
                      disabled={
                        filterVariables.selectedPartnerId &&
                        filterVariables.selectedPartnerId !== -1
                          ? false
                          : true
                      }
                      placeholder="Select partner branch"
                      value={filterVariables.selectedPartnerBranch}
                      onChange={(e) => {
                        handleFilterChange(e, "selectedPartnerBranch");
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-1 md:col-span-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 md:col-span-3">
                    <p className="text-base">Agent:</p>
                  </div>
                  <Select
                    defaultOption={{
                      name: "-- Select Agent --",
                      value: -1,
                    }}
                    items={agentList}
                    disabled={
                      filterVariables.selectedPartnerBranch?.length > 0 ||
                      filterVariables.selectedPartnerBranch == -1
                        ? false
                        : true
                    }
                    className={"col-span-1 md:col-span-5"}
                    placeholder="Select partner agent"
                    value={filterVariables.selectedPartnerAgent}
                    onChange={(e) => {
                      handleFilterChange(e, "selectedPartnerAgent");
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <h2 className={"font-bold"}>Transaction Details</h2>

          {/* Transaction Number */}
          <div className="col-span-1 md:col-span-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <div className="col-span-1 md:col-span-3">
                <p className="text-base">Transaction Number:</p>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Textfield
                  value={filterVariables.transactionNumber}
                  onChange={(e) => {
                    handleFilterChange(e, "transactionNumber");
                  }}
                  // inputClassName=`w-full`}
                  placeholder="Enter transaction number"
                />
              </div>
            </div>
          </div>

          {/* Transaction Purpose */}
          <div className="col-span-1 md:col-span-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <div className="col-span-1 md:col-span-3">
                <p className="text-base">Trigger Type:</p>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Select
                  items={[
                    { name: "-- All trigger types --", value: -1 },
                    ...transactionPurposes,
                  ]}
                  value={filterVariables.transactionPurposeId}
                  onChange={(e) => {
                    handleFilterChange(e, "transactionPurposeId");
                  }}
                  // inputClassName=`w-full`}
                  placeholder="Select purpose"
                />
              </div>
            </div>
          </div>

          {/* Transaction Type */}
          <div className="col-span-1 md:col-span-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <div className="col-span-12 md:col-span-3">
                <p className="text-base">Transaction Type:</p>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Select
                  items={[
                    { name: "-- All transaction type --", value: -1 },
                    ...transactionTypes,
                  ]}
                  value={filterVariables.transactionTypeId}
                  onChange={(e) => {
                    handleFilterChange(e, "transactionTypeId");
                  }}
                  // inputClassName=`w-full`}
                  placeholder="Select type"
                />
              </div>
            </div>
          </div>

          {/* Transaction Date Range */}
          <h2 className={"font-bold pt-2"}>Transaction Date Range</h2>
          <div className="grid grid-cols-8 pl-4 items-center space-y-1">
            <div className="col-span-8 md:col-span-4">
              <PrimereactDateRange
                className="custom-calendar-button"
                startDate={filterVariables.startDate}
                endDate={filterVariables.endDate}
                startDateName="startDate"
                endDateName="endDate"
                onChange={(e, fieldName) => {
                  handleFilterChange(e, fieldName);
                }}
                // inputClassName=`w-full`}
                readOnlyInput={true}
                max={new Date()}
                min={new Date("2021-01-01")}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="col-span-12">
          <div className="flex mt-4 items-center space-x-2">
            <Button
              icon="pi pi-undo"
              // parentClassName=`col-span-1`
              onClick={handleReset}
              className="flex p-2 items-center gap-2 border border-primary-primaryYellow bg-white text-black hover:bg-secondary-lightestGray font-normal"
              value="Reset"
            />
            <Button
              // parentClassName=`col-span-1`}
              onClick={() => handleSearch()}
              icon="pi pi-search"
              className="flex p-2 items-center gap-2 bg-primary-primaryYellow text-secondary-purpleGray border-0 font-semibold hover:bg-primary-primaryYellowDark"
              value="Search"
            />
          </div>
        </div>
      </div>

      {isClicked && (
        <div className="mt-5">
          <PartnerWalletSummaryCards
            transactions={transformedTransactions}
            searchedAccountId={getAccountIdByFilter(
              filterVariables.walletFilter,
              filterVariables,
              operatorId,
            )}
          />
          {loadingWalletReport ? (
            <Skeleton totalColumns={8} />
          ) : (
            <Card>
              {transformedTransactions && transformedTransactions.length > 0 ? (
                <PartnerWalletTable
                  data={transformedTransactions}
                  filterValues={filterVariables}
                  headerClassName="bg-primary-primaryYellow text-secondary-purpleGray"
                  allPartners={partnerList}
                  allBranches={branchList ? branchList : []}
                  allAgents={agentList ? agentList : []}
                  operatorName={operatorName}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No transactions found for the selected filters.
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </ContentContainer>
  );
};

export default WalletHistoryReport;
